import { Mail, Phone, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { contact } from "../config/pitch";
import { usePitch } from "../context/PitchContext";
import { Reveal, Eyebrow } from "./Reveal";
import { LogoMark } from "./Logo";

const Action = ({ href, icon: Icon, label, value, testId }) => (
  <motion.a
    href={href}
    data-testid={testId}
    whileHover={{ y: -4 }}
    whileTap={{ scale: 0.98 }}
    className="group flex items-center justify-between gap-4 rounded-2xl border border-[#F4EDE3]/20 bg-[#10223a] p-6 transition-colors duration-300 hover:border-[#C87D55] hover:bg-[#C87D55]"
  >
    <span className="flex items-center gap-4">
      <Icon className="h-5 w-5 shrink-0 text-[#D4A373] transition-colors duration-300 group-hover:text-[#0B192C]" />
      <span>
        <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300 transition-colors duration-300 group-hover:text-[#0B192C]">{label}</span>
        <span className="mt-1 block break-all text-lg text-[#F4EDE3] transition-colors duration-300 group-hover:text-[#0B192C] md:text-xl">{value}</span>
      </span>
    </span>
    <ArrowUpRight className="h-5 w-5 shrink-0 text-[#F4EDE3] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-[#0B192C]" />
  </motion.a>
);

export const Cta = () => {
  const pitch = usePitch();
  const subject = encodeURIComponent(`MarineOS: next step for ${pitch.vesselName}`);
  return (
  <section id="talk" data-testid="cta-section" className="relative overflow-hidden bg-[#0B192C] px-6 pb-10 pt-24 text-[#F4EDE3] md:px-10 md:pt-36">
    <img src={pitch.closingImage} alt="" className="absolute inset-0 h-full w-full object-cover object-[center_70%] opacity-25" />
    <div className="grain absolute inset-0" />
    <div className="relative mx-auto max-w-7xl">
      <div className="grid gap-14 lg:grid-cols-[1.3fr_1fr] lg:items-end">
        <Reveal>
          <Eyebrow light>The next step</Eyebrow>
          <h2 className="mt-5 font-display text-6xl leading-none tracking-tight md:text-8xl lg:text-9xl">
            Let's <span className="italic text-[#D4A373]">talk.</span>
          </h2>
          <p className="mt-8 max-w-md text-base text-slate-300 md:text-lg">
            The next step for {pitch.vesselName}, after our meeting. Email or call.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="space-y-4">
          <Action testId="cta-email-link" href={`mailto:${contact.email}?subject=${subject}`} icon={Mail} label="Email" value={contact.email} />
          <Action testId="cta-phone-link" href={contact.phoneHref} icon={Phone} label="Mobile" value={contact.phoneDisplay} />
        </Reveal>
      </div>
      <footer className="mt-24 flex flex-col gap-4 border-t border-[#F4EDE3]/15 pt-8 text-sm text-slate-300 md:flex-row md:items-center md:justify-between">
        <span className="flex items-center gap-3">
          <LogoMark className="h-7 w-7" />
          MarineOS™ is a Navscora product.
        </span>
        <span data-testid="footer-company">© {new Date().getFullYear()} {contact.company} · Prepared for {pitch.customerName}</span>
      </footer>
    </div>
  </section>
  );
};
